/**
 * FileDropzone.tsx
 * @version 2.8 — diseño aprobado: contenedor 46px fondo oscuro, trazo 1.5, sin botón
 */

import { useState, useRef, useCallback, type DragEvent, type ChangeEvent, type FC } from 'react'
import '../../home/home-v2.css'
import { ACCEPT_ATTR, FORMAT_TAGS } from '../../../lib/acceptedFormats'
// SERP2: la misma serpiente que P1; por ruta de fichero (HX-MOCK-FACTORY-BARREL-COUPLING).
import { SpinnerSnake } from '../../../design-system/v2/components/SpinnerSnake'

export interface FileDropzoneProps {
  variant?: 'prominent' | 'compact'
  isProcessing?: boolean
  processingStatus?: string
  error?: string | null
  onFileSelect?: (file: File) => void
  onReset?: () => void
  currentFileName?: string
}

export const FileDropzone: FC<FileDropzoneProps> = ({
  variant = 'prominent',
  isProcessing = false,
  processingStatus = '',
  error = null,
  onFileSelect,
  onReset,
  currentFileName
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && onFileSelect) onFileSelect(file)
    if (e.target) e.target.value = ''
  }, [onFileSelect])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    if (!isProcessing) setIsDragging(true)
  }, [isProcessing])

  const handleDragLeave = useCallback(() => setIsDragging(false), [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (isProcessing) return
    const file = e.dataTransfer.files[0]
    if (file && onFileSelect) onFileSelect(file)
  }, [isProcessing, onFileSelect])

  // ── COMPACT ───────────────────────────────────────────────────────
  if (variant === 'compact') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {currentFileName && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', background: 'rgba(45,212,191,0.1)',
            borderRadius: 6, fontSize: 12, color: '#2DD4BF',
            fontFamily: "'JetBrains Mono', monospace"
          }}>
            {currentFileName}
          </span>
        )}
        <button
          onClick={onReset}
          title="Cancelar y cargar otro documento"
          style={{
            padding: '6px 12px', background: 'transparent',
            border: '1px solid #3f3f3c', borderRadius: 6,
            color: '#a1a1aa', fontSize: 12, cursor: 'pointer',
            fontFamily: "'Space Grotesk', sans-serif",
          }}
        >
          Cancelar documento
        </button>
        <input ref={fileInputRef} type="file" hidden accept={ACCEPT_ATTR} onChange={handleFileChange} />
      </div>
    )
  }

  // ── PROMINENT ────────────────────────────────────────────────────
  return (
    <div className="home-page" style={{ background: 'transparent', minHeight: 'auto' }}>
      <div
        className={`dropzone ${isDragging ? 'dragover' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ position: 'relative', cursor: isProcessing ? 'wait' : 'pointer', textAlign: 'center' }}
      >
        {/* Input transparente */}
        {!isProcessing && (
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            onChange={handleFileChange}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }}
          />
        )}

        {/* Icono — diseño aprobado: 46px, fondo oscuro, trazo fino 1.5 */}
        <div style={{
          width: 46,
          height: 46,
          borderRadius: 10,
          background: 'rgba(30,42,38,0.9)',
          border: '1px solid rgba(45,212,191,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#2dd4bf',
          margin: '0 auto 14px',
          pointerEvents: 'none',
        }}>
          {isProcessing
            ? <SpinnerSnake size={20} label="Procesando el documento" />
            : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
              </svg>
            )
          }
        </div>

        {/* Título */}
        <div style={{
          fontSize: 15, fontWeight: 600, marginBottom: 12, pointerEvents: 'none',
          fontFamily: "'Space Grotesk', sans-serif", color: '#fafaf9'
        }}>
          {isProcessing ? processingStatus || 'Procesando...' : 'Arrastra tu documento PTEL aquí'}
        </div>

        {/* Formatos */}
        <div className="dropzone__formats" style={{ pointerEvents: 'none' }}>
          {FORMAT_TAGS.map(tag => (
            <span key={tag} className="format-tag">{tag}</span>
          ))}
        </div>

        {/* Privacidad */}
        <div className="dropzone__privacy" style={{ pointerEvents: 'none' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="#2dd4bf" strokeWidth="2" style={{ flexShrink: 0 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          100% en tu navegador · Tus datos nunca salen de tu equipo
        </div>

        {/* Error */}
        {error && (
          <div style={{
            marginTop: 12, padding: '8px 12px',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8, color: '#ef4444', fontSize: 13, pointerEvents: 'none'
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

export default FileDropzone
